export type VideoProvider = 'heygen' | 'seedance' | 'composition' | 'uploaded';
export type VideoStatus = 'pending' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
export type SeedanceMode = 'text2video' | 'image2video';

export interface GeneratedVideo {
  id: string;
  user_id: string;
  project_id?: string;
  campaign_id?: string;
  title?: string;
  prompt?: string;
  provider: VideoProvider;
  mode?: SeedanceMode | 'avatar' | null;
  scenes?: VideoScene[];
  parameters?: Record<string, any>;
  external_job_id?: string | null;
  original_url?: string | null;
  thumbnail_url?: string | null;
  s3_key?: string | null;
  duration_seconds?: number | null;
  width?: number | null;
  height?: number | null;
  file_size?: number | null;
  mime_type?: string;
  aspect_ratio?: string;
  credits_cost: number;
  tags?: string[];
  is_favorite: boolean;
  status: VideoStatus;
  progress: number;
  error_message?: string | null;
  metadata?: Record<string, any>;
  campaign?: { id: string; name: string };
  folder_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface VideoScene {
  id: string;
  type: 'video' | 'image' | 'text';
  // Either reference an existing video record or supply a direct URL
  source_video_id?: string;
  url?: string;
  thumbnail_url?: string;
  duration: number; // seconds
  prompt?: string;
  provider?: VideoProvider;
  // Effects/transitions for the editor
  transition?: 'cut' | 'fade' | 'slide';
  text_overlay?: {
    text: string;
    position: 'top' | 'center' | 'bottom';
    color: string;
    font_size: number;
  };
  // Status of generation (when scene was AI-generated separately)
  status?: VideoStatus;
}

// ============================================
// Premiere-like timeline (v2)
// ============================================

export type ClipKind = 'video' | 'audio' | 'text' | 'image';
export type Position = 'top' | 'center' | 'bottom';
export type FontFamily = 'Inter' | 'Arial' | 'Georgia' | 'Courier New' | 'Impact' | 'Comic Sans MS';

export interface VideoClip {
  id: string;
  kind: 'video';
  source_video_id?: string;
  url: string;
  thumbnail_url?: string;
  start: number;          // position on timeline (seconds)
  duration: number;       // length on timeline (seconds)
  trim_start?: number;    // skip first N seconds of source
  filter?: string;        // color preset id
  muted: boolean;         // mute the source video's native audio
  volume: number;         // 0..1
  transition?: 'cut' | 'fade' | 'slide' | 'wipe' | 'zoom' | 'blur' | 'spin' | 'flip' | 'iris';
  transition_duration?: number;  // seconds, default 0.5
  lane?: number;          // vertical layer within the video track (0 = bottom)
}

export interface AudioClip {
  id: string;
  kind: 'audio';
  url: string;
  name: string;
  start: number;
  duration: number;
  trim_start?: number;
  volume: number;         // 0..1
  fade_in?: number;       // seconds
  fade_out?: number;      // seconds
  lane?: number;          // vertical layer within the audio track
  detached_from?: string; // ID of the video clip this was detached from (if any)
}

export interface TextOverlayClip {
  id: string;
  kind: 'text';
  text: string;
  start: number;
  duration: number;
  position: Position;             // legacy: used as fallback when *_pct is absent
  position_x_pct?: number;        // 0..100, centre of the text block on the stage
  position_y_pct?: number;        // 0..100
  color: string;
  background?: string;            // 'transparent' | rgba string
  font_size: number;              // design px @ 720 reference height — preview/export scale to canvas height
  font_family: FontFamily;
  bold?: boolean;
  italic?: boolean;
  lane?: number;
  transition?: 'cut' | 'fade' | 'slide' | 'wipe' | 'zoom' | 'blur' | 'spin' | 'flip' | 'iris';
  transition_duration?: number;  // seconds, default 0.5
}

export interface ImageOverlayClip {
  id: string;
  kind: 'image';
  url: string;
  start: number;
  duration: number;
  width_pct: number;          // 1..100 — width as % of stage
  position_x_pct: number;     // 0..100 — center position
  position_y_pct: number;
  opacity: number;            // 0..1
  lane?: number;
  transition?: 'cut' | 'fade' | 'slide' | 'wipe' | 'zoom' | 'blur' | 'spin' | 'flip' | 'iris';
  transition_duration?: number;  // seconds, default 0.5
}

// A subtitle track — single block on the timeline holding many cues with shared style.
// Multiple tracks allowed (e.g. one per language).
export interface CaptionCue {
  id: string;
  start: number;   // seconds, relative to track.start
  end: number;     // seconds, relative to track.start
  text: string;
}

export interface CaptionTrack {
  id: string;
  kind: 'caption-track';
  start: number;
  duration: number;       // computed: max(cues.end), but stored
  lane?: number;
  language?: string;      // 'pt', 'en', etc — to distinguish multiple tracks
  name?: string;          // user label, e.g. "Legendas PT"
  style: {
    position: Position;
    color: string;
    background?: string;
    font_size: number;
    font_family: FontFamily;
    bold?: boolean;
    italic?: boolean;
  };
  cues: CaptionCue[];
}

export type AnyClip = VideoClip | AudioClip | TextOverlayClip | ImageOverlayClip | CaptionTrack;

export interface TimelineProject {
  ratio: '16:9' | '9:16' | '1:1' | '4:3';
  videoClips: VideoClip[];
  audioClips: AudioClip[];
  overlayClips: (TextOverlayClip | ImageOverlayClip)[];
}

export interface VideoCreditBalance {
  total: number;
  used: number;
  available: number;
  monthly_free: number;
  monthly_reset_at?: string;
}

export interface VideoCreditTransaction {
  id: string;
  type: 'debit' | 'credit' | 'refund' | 'grant';
  amount: number;
  source: string;
  description?: string;
  external_ref?: string;
  balance_after?: number;
  video_id?: string;
  created_at: string;
}

export interface CreditPack {
  credits: number;
  price_brl: number;
}

export type CreditPackId = 'starter' | 'pro' | 'studio';

export interface VideoEstimate {
  cost: number;
  balance: VideoCreditBalance;
  sufficient: boolean;
}

export interface VideoGenerateParams {
  provider: VideoProvider;
  prompt?: string;
  mode?: SeedanceMode | 'avatar';
  parameters?: {
    duration?: number;
    resolution?: '480p' | '720p' | '1080p' | '4k';
    /** task_type do Seedance (ex.: 'seedance-2.5'). Default do servidor quando ausente. */
    model?: string;
    image_url?: string;
    image_urls?: string[];
    camera_fixed?: boolean;
    seed?: number;
    avatar_id?: string;
    voice_id?: string;
    avatar_tier?: 'standard' | 'premium';
    background?: string;
    caption?: boolean;
    regenerated_from?: string;
  };
  scenes?: VideoScene[];
  aspect_ratio?: '16:9' | '9:16' | '1:1' | '4:3';
  title?: string;
  campaign_id?: string;
}

export interface HeygenAvatar {
  id: string;
  name: string;
  preview_image_url?: string;
  preview_video_url?: string;
  gender?: string;
  premium?: boolean;
  is_custom?: boolean;
  is_yours?: boolean;
}

export interface HeygenVoice {
  id: string;
  name: string;
  language?: string;
  gender?: string;
  preview_audio?: string;
  support_locale?: string;
}

/** Um modelo do Seedance exposto pelo backend (/providers/status). */
export interface SeedanceModelInfo {
  id: string;
  label: string;
  generation: string;
  tier: 'draft' | 'premium' | string;
  has_audio: boolean;
  min_duration: number;
  max_duration: number;
  resolutions: string[];
  max_images: number;
  modes: string[];
  recommended?: boolean;
}

/**
 * Fallback usado enquanto /providers/status não respondeu (ou em backend
 * antigo, que não manda a lista). Precisa espelhar o MODELS do
 * backend/src/services/seedanceService.js.
 */
export const SEEDANCE_MODELS_FALLBACK: SeedanceModelInfo[] = [
  {
    id: 'seedance-2.5', label: 'Seedance 2.5', generation: '2.5', tier: 'premium',
    has_audio: true, min_duration: 4, max_duration: 30,
    resolutions: ['480p', '720p', '1080p'], max_images: 9,
    modes: ['text2video', 'image2video'], recommended: true
  },
  {
    id: 'seedance-2', label: 'Seedance 2.0 Pro', generation: '2.0', tier: 'premium',
    has_audio: true, min_duration: 4, max_duration: 15,
    resolutions: ['480p', '720p', '1080p'], max_images: 5,
    modes: ['text2video', 'image2video']
  },
  {
    id: 'seedance-2-fast', label: 'Seedance 2.0 Fast', generation: '2.0', tier: 'draft',
    has_audio: true, min_duration: 4, max_duration: 15,
    resolutions: ['480p', '720p'], max_images: 5,
    modes: ['text2video', 'image2video']
  }
];

export interface ProviderStatus {
  heygen: { configured: boolean };
  seedance: {
    configured: boolean;
    default_model?: string;
    models?: SeedanceModelInfo[];
  };
  // Render server-side (ffmpeg na fila) — quando disponível, o modal de export
  // oferece "Renderizar no servidor" como padrão.
  server_render?: { available: boolean };
}

export const ASPECT_RATIOS: { value: string; label: string; w: number; h: number }[] = [
  { value: '16:9', label: 'Widescreen 16:9', w: 1920, h: 1080 },
  { value: '9:16', label: 'Vertical 9:16', w: 1080, h: 1920 },
  { value: '1:1', label: 'Square 1:1', w: 1080, h: 1080 },
  { value: '4:3', label: 'Classic 4:3', w: 1440, h: 1080 }
];

export const RESOLUTIONS: { value: string; label: string }[] = [
  { value: '480p', label: '480p — fastest' },
  { value: '720p', label: '720p — recommended' },
  { value: '1080p', label: '1080p — Full HD' }
];
